import { useState, useCallback } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, getGuestId, initAnonymousAuth } from "../lib/firebase";
import { useCartStore } from "../store/cartStore";
import { parsePriceToNumber } from "../lib/currencyService";
import { buildFoonPath } from "../utils/routes";

export function useCheckout(currency, rates) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const cartItems = useCartStore((state) => state.cartItems);

  const submitOrder = useCallback(async () => {
    if (cartItems.length === 0) {
      setError("Your cart is empty");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await initAnonymousAuth();
      const guestId = getGuestId();
      if (!guestId) {
        throw new Error("Unable to establish guest session");
      }

      const missingPrice = cartItems.find((item) => !item.stripePriceId);
      if (missingPrice) {
        throw new Error(
          `${missingPrice.name} is missing a Stripe price ID. Please contact support.`,
        );
      }

      const rate = rates?.[currency] || 1;
      const items = cartItems.map((item) => ({
        productId: String(item.id),
        name: item.name,
        selectedColor: item.selectedColor || null,
        qty: item.quantity ?? 1,
        priceAtPurchase: parsePriceToNumber(item.price) * rate,
        currencyUsed: currency,
        stripePriceId: item.stripePriceId,
      }));

      const totalAmount = items.reduce(
        (sum, item) => sum + item.priceAtPurchase * item.qty,
        0,
      );

      const order = {
        guestId,
        items,
        totalAmount,
        currency,
        shippingDetails: null,
        status: "pending",
        timestamp: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "orders"), order);

      const origin = window.location.origin;
      const successUrl = `${origin}${buildFoonPath("/order/success")}?session_id={CHECKOUT_SESSION_ID}&order_id=${docRef.id}`;
      const cancelUrl = `${origin}${buildFoonPath("/order/cancel")}?order_id=${docRef.id}`;

      const createSession = httpsCallable(functions, "createCheckoutSession");
      const { data } = await createSession({
        orderId: docRef.id,
        successUrl,
        cancelUrl,
      });

      if (!data?.url) {
        throw new Error("Stripe session URL was not returned");
      }
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || "Checkout failed");
      setIsSubmitting(false);
    }
  }, [cartItems, currency, rates]);

  return { submitOrder, isSubmitting, error };
}
